import React, { useState } from 'react';
import { activePb } from '../lib/pb';
import { useData } from '../data/DataProvider';
import { usePortalClient } from './usePortalClient';
import FileDropzone from '../shared/components/FileDropzone';
import { useToast } from '../shared/components/ToastProvider';
import { pbErrorMessage } from '../lib/errors';

// Pass 6 §6B — "Request a job": creates a jobs record with status
// `requested` (the pb_hook clamps status + forces tenant/site address
// server-side); photos attach right after create, like the admin flow.
// Extracted from the portal Dashboard (update_tasks.md §7.15) so it can
// also render on its own dedicated route.
export default function RequestJob() {
  const { add, update } = useData();
  const { client } = usePortalClient();
  const { notify } = useToast();
  const [requesting, setRequesting] = useState(false);
  const [requestForm, setRequestForm] = useState({ title: '', description: '', preferred: '', note: '' });
  const [requestFiles, setRequestFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submitRequest = async (event) => {
    event.preventDefault();
    if (!requestForm.title.trim()) {
      notify('Describe the job you need', 'error');
      return;
    }
    setBusy(true);
    try {
      const ui = await add('jobs', {
        client_id: client.id,
        title: requestForm.title.trim(),
        description: requestForm.description.trim(),
        status: 'requested',
        notes: requestForm.note.trim(),
        client_requested_date: requestForm.preferred || null,
        client_request_note: requestForm.note.trim() || null,
      });
      if (requestFiles.length) {
        const form = new FormData();
        requestFiles.forEach((file) => form.append('photos', file));
        const record = await activePb().collection('jobs').update(ui.id, form, { expand: 'engineer' });
        update('jobs', ui.id, { photos: Array.isArray(record.photos) ? record.photos.filter(Boolean) : [] });
      }
      setRequestForm({ title: '', description: '', preferred: '', note: '' });
      setRequestFiles([]);
      setRequesting(false);
      notify('Request sent — we will come back to you with a date.');
    } catch (err) {
      // Surface PB's field-level detail ("title: cannot be blank") instead
      // of the generic "Failed to create record." users could not act on.
      notify(pbErrorMessage(err, 'Could not send the request'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="portal-section-head">
        <div>
          <span className="eyebrow">Need some work doing?</span>
          <h2>Request a job</h2>
        </div>
        {!requesting && <button className="secondary-button" onClick={() => setRequesting(true)}>+ New request</button>}
      </div>

      {requesting && (
        <section className="portal-card">
          <form className="drawer-form" onSubmit={submitRequest}>
            <label>What do you need?<input value={requestForm.title} onChange={(event) => setRequestForm((f) => ({ ...f, title: event.target.value }))} placeholder="e.g. Leaking radiator, annual service…" required /></label>
            <label>Details<textarea rows="3" value={requestForm.description} onChange={(event) => setRequestForm((f) => ({ ...f, description: event.target.value }))} placeholder="Anything we should know — symptoms, location, access…" /></label>
            <label>Preferred date (optional)<input type="date" value={requestForm.preferred} onChange={(event) => setRequestForm((f) => ({ ...f, preferred: event.target.value }))} /></label>
            <label>Notes for the team<input value={requestForm.note} onChange={(event) => setRequestForm((f) => ({ ...f, note: event.target.value }))} placeholder="Optional" /></label>
            <FileDropzone
              onFiles={setRequestFiles}
              accept="image/jpeg,image/png,image/webp"
              hint="Optional — photos help us diagnose before we visit"
              onReject={(msgs) => notify(msgs.join('. '), 'error')}
            />
            {requestFiles.length ? <p className="field-hint">{requestFiles.length} photo{requestFiles.length > 1 ? 's' : ''} ready to attach</p> : null}
            <div className="drawer-actions">
              <button type="button" className="secondary-button" onClick={() => { setRequesting(false); setRequestFiles([]); }}>Cancel</button>
              <button className="primary-button" disabled={busy}>{busy ? 'Sending…' : 'Send request'}</button>
            </div>
          </form>
        </section>
      )}
    </>
  );
}
